import type { TenantRole } from '@servicetim/shared';
import { buttonVariants, EmptyState } from '@servicetim/ui/components';
import { hasTenantRole, useSessionClaims } from '@servicetim/ui/lib';
import { Link } from '@tanstack/react-router';
import { ShieldOff } from 'lucide-react';
import type { ReactNode } from 'react';

export function NotAllowed({ minimum }: { minimum?: TenantRole }) {
  return (
    <EmptyState
      icon={ShieldOff}
      title="You are not allowed to open this page"
      description={
        minimum
          ? `This section needs the tenant role "${minimum}" or higher. Ask a tenant admin to change your membership role.`
          : 'Your tenant role does not grant access to this section.'
      }
      action={
        <Link to="/" className={buttonVariants({ variant: 'outline' })}>
          Back to the dashboard
        </Link>
      }
    />
  );
}

/**
 * Client-side gate for admin-only sections (the server enforces the same rule with
 * `requireTenantRole`). The nav hides these entries; a direct URL shows the not-allowed state.
 */
export function RequireTenantRole({
  minimum,
  children,
}: {
  minimum: TenantRole;
  children: ReactNode;
}) {
  const { tenantRole } = useSessionClaims();
  if (!hasTenantRole(tenantRole, minimum)) return <NotAllowed minimum={minimum} />;
  return children;
}
